import { getServerUrl } from '../utils/getServerUrl';

import { PlanType } from '../../types';

import { useQuery } from '@tanstack/react-query';

import { useQueryAccount } from './useQueryAccount';
import { useQueryAccountPlan } from '../..//universal-layout/hooks';
import { readCookie } from '@zapier/cookies';

async function fetchTopNavData({
  currentAccountId,
  customUserId,
  isTrialing,
  allowTeams,
  planType,
  bffHostname,
}: {
  currentAccountId?: number;
  customUserId?: number;
  isTrialing?: boolean;
  allowTeams?: boolean;
  planType?: PlanType;
  bffHostname?: string;
}) {
  const serverUrl = getServerUrl(bffHostname || '');
  const topnavUrl = new URL(`${serverUrl}/api/v3/navigation-data/topbar`);

  if (typeof isTrialing === 'boolean') {
    topnavUrl.searchParams.append('is-trialing', isTrialing.toString());
  }

  if (typeof allowTeams === 'boolean') {
    topnavUrl.searchParams.append('allow-teams', allowTeams.toString());
  }

  if (typeof planType === 'string') {
    topnavUrl.searchParams.append('plan-type', planType.toLowerCase());
  }

  const response = await fetch(topnavUrl.toString(), {
    credentials: 'include',
    headers: {
      'x-current-account-id': currentAccountId?.toString() || '',
      'x-customuser-id': customUserId?.toString() || '',
      'x-visitor-id': readCookie('visitor_id') ?? '',
    },
  });

  if (!response.ok) {
    throw new Error('Response was not OK');
  }

  const topNavJSON = await response.json();
  return topNavJSON.data;
}

export const useQueryTopNav = ({
  apiHostname,
  bffHostname,
  currentAccountId,
  customUserId,
  isInAppExperience,
}: {
  apiHostname: string;
  bffHostname: string;
  currentAccountId: number | undefined;
  customUserId: number | undefined;
  isInAppExperience: boolean;
}) => {
  const {
    data: accountData,
    isLoading: isLoadingAccountData,
    error: accountDataError,
  } = useQueryAccount({ apiHostname, currentAccountId });
  const {
    data: planData,
    isLoading: isLoadingPlanData,
    error: planDataError,
  } = useQueryAccountPlan(currentAccountId, accountData?.plan?.id, apiHostname);

  const isTrialing = accountData?.isTrialing;
  const allowTeams = accountData?.plan?.allowTeams;
  const planType = planData?.planType;

  // For in-app pages, make sure we have all the account data we need before enabling the query to avoid content flashing
  // Enable by default for other pages
  const isEnabled = isInAppExperience
    ? !isLoadingAccountData &&
      !isLoadingPlanData &&
      !planDataError &&
      !accountDataError &&
      typeof currentAccountId === 'number' &&
      typeof customUserId === 'number'
    : true;

  return useQuery({
    queryKey: [
      'topnav',
      currentAccountId,
      customUserId,
      isTrialing,
      allowTeams,
      planType,
    ],
    queryFn: () =>
      fetchTopNavData({
        currentAccountId,
        customUserId,
        isTrialing,
        allowTeams,
        planType,
        bffHostname,
      }),
    enabled: isEnabled,
    refetchOnWindowFocus: false,
    retry: false,
  });
};
